import { createUrl } from "@/redux/api";
import { BuilderType, CursorPage } from "@/redux/types";
import {
  BulkMarkReadRequest,
  CreateTopicNotificationSettingRequest,
  EmailSettingResponse,
  MarkReadResponse,
  NotificationListQueryParams,
  NotificationResponse,
  NotificationSettingResponse,
  NotificationTagTypes,
  NotificationTags,
  PatchEmailSettingRequest,
  PatchNotificationSettingRequest,
  PatchTopicNotificationSettingRequest,
  TopicNotificationSettingResponse,
  UnreadCountResponse,
} from "./types";

const notificationsService = (builder: BuilderType<NotificationTags>) => ({
  getNotifications: builder.query<
    CursorPage<NotificationResponse>,
    NotificationListQueryParams | void
  >({
    query: (params) => createUrl("/notifications/", params ?? undefined),
    providesTags: (result): NotificationTagTypes[] =>
      result
        ? [
            ...result.results.map(
              (n): NotificationTagTypes => ({
                type: "Notifications",
                id: n.id,
              }),
            ),
            { type: "Notifications" },
          ]
        : [{ type: "Notifications" }],
  }),
  getUnreadNotificationsCount: builder.query<UnreadCountResponse, void>({
    query: () => ({
      url: "/notifications/unread-count/",
      method: "GET",
    }),
    providesTags: [{ type: "Notifications" }],
  }),
  bulkMarkNotificationsRead: builder.mutation<
    MarkReadResponse,
    BulkMarkReadRequest
  >({
    query: (data) => ({
      url: "/notifications/mark-read/",
      method: "POST",
      data,
    }),
    invalidatesTags: [{ type: "Notifications" }],
  }),
  markAllNotificationsRead: builder.mutation<MarkReadResponse, void>({
    query: () => ({
      url: "/notifications/mark-all-read/",
      method: "POST",
    }),
    invalidatesTags: [{ type: "Notifications" }],
  }),
  getNotificationSettingsForUser: builder.query<
    NotificationSettingResponse[],
    void
  >({
    query: () => ({
      url: "/notifications/settings/",
      method: "GET",
    }),
    providesTags: (result): NotificationTagTypes[] =>
      result
        ? [
            ...result.map(
              (s): NotificationTagTypes => ({
                type: "NotificationSettings",
                id: s.id,
              }),
            ),
            { type: "NotificationSettings" },
          ]
        : [],
  }),
  patchNotificationSettings: builder.mutation<
    NotificationSettingResponse,
    { id: number; data: PatchNotificationSettingRequest }
  >({
    query: ({ id, data }) => ({
      url: `/notifications/settings/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: [{ type: "NotificationSettings" }],
  }),
  getEmailSettingsForUser: builder.query<EmailSettingResponse[], void>({
    query: () => ({
      url: "/emails/settings/",
      method: "GET",
    }),
    providesTags: (result): NotificationTagTypes[] =>
      result
        ? [
            ...result.map(
              (s): NotificationTagTypes => ({
                type: "EmailSettings",
                id: s.id,
              }),
            ),
            { type: "EmailSettings" },
          ]
        : [],
  }),
  patchEmailSettings: builder.mutation<
    EmailSettingResponse,
    { id: number; data: PatchEmailSettingRequest }
  >({
    query: ({ id, data }) => ({
      url: `/emails/settings/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: [{ type: "EmailSettings" }],
  }),
  getTopicNotificationSettingsForUser: builder.query<
    TopicNotificationSettingResponse[],
    void
  >({
    query: () => ({
      url: "/notifications/topic-settings/",
      method: "GET",
    }),
    providesTags: (result): NotificationTagTypes[] =>
      result
        ? [
            ...result.map(
              (s): NotificationTagTypes => ({
                type: "TopicNotificationSettings",
                id: s.id,
              }),
            ),
            { type: "TopicNotificationSettings" },
          ]
        : [],
  }),
  createTopicNotificationSettings: builder.mutation<
    TopicNotificationSettingResponse,
    CreateTopicNotificationSettingRequest
  >({
    query: (data) => ({
      url: "/notifications/topic-settings/",
      method: "POST",
      data,
    }),
    invalidatesTags: [{ type: "TopicNotificationSettings" }],
  }),
  patchTopicNotificationSettings: builder.mutation<
    TopicNotificationSettingResponse,
    { id: number; data: PatchTopicNotificationSettingRequest }
  >({
    query: ({ id, data }) => ({
      url: `/notifications/topic-settings/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: [{ type: "TopicNotificationSettings" }],
  }),
});

export default notificationsService;
